/**
 * Shared ReactMarkdown component configuration.
 *
 * Headings generate slugified `id` attributes so fragment links
 * (e.g. /docs/runs#streaming) scroll to the right section.
 */

import type { ReactNode } from "react";
import type { Components } from "react-markdown";
import { CodeBlock } from "./code-block";
import { MarkdownImage } from "./markdown-image";
import { MarkdownTable } from "./markdown-table";

/** Extract plain text from React children and convert to a URL-safe slug. */
function slugify(children: ReactNode): string {
	const text = extractText(children);
	return text
		.toLowerCase()
		.replace(/[^\w\s-]/g, "")
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-")
		.trim();
}

function extractText(node: ReactNode): string {
	if (typeof node === "string") return node;
	if (typeof node === "number") return String(node);
	if (Array.isArray(node)) return node.map(extractText).join("");
	if (node && typeof node === "object" && "props" in node) {
		const props = node.props as { children?: ReactNode };
		return extractText(props.children);
	}
	return "";
}

export const markdownComponents: Components = {
	h1: ({ children }) => <h1 id={slugify(children)}>{children}</h1>,
	h2: ({ children }) => <h2 id={slugify(children)}>{children}</h2>,
	h3: ({ children }) => <h3 id={slugify(children)}>{children}</h3>,
	h4: ({ children }) => <h4 id={slugify(children)}>{children}</h4>,
	a: ({ href, children }) => {
		const isExternal = href?.startsWith("http");
		return (
			<a href={href} {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}>
				{children}
			</a>
		);
	},
	pre: ({ children }) => <div className="not-prose">{children}</div>,
	code: CodeBlock,
	img: MarkdownImage,
	table: MarkdownTable,
};
